import { Col, Form, Input, message, Modal, Row } from "antd";
import { Rule } from "antd/lib/form";
import { leadApi } from "api/lead.api";
import { AppButton, AppSelect } from "components/antd-ui";
import { observer } from "mobx-react";
import React, { useCallback, useImperativeHandle, useState } from "react";
import { Lead, LeadSource, LeadStatus } from "types/lead";
import { ModalStatus } from "types/modal";
import { can } from "utils/auth";
import { formatDateTime } from "utils/date";
import { LEAD_PATH, leadSourceLabel, leadSourceOptions, leadStatusOptions, parseLeadLinks } from "../lead.meta";
import { LeadFromLinks } from "./LeadFromLinks";

const requiredRules: Rule[] = [{ required: true, message: "Bắt buộc" }];

interface LeadForm {
  name?: string;
  phone: string;
  zalo?: string;
  category?: string;
  estimatedBudget?: string;
  status: LeadStatus;
  source: LeadSource;
  description?: string;
}

export interface LeadModal {
  handleCreate: () => void;
  handleUpdate: (lead: Lead) => void;
  handleView: (lead: Lead) => void;
}

interface LeadModalProps {
  onClose: () => void;
  onSubmitOk: () => void;
}

const toForm = (lead: Lead): LeadForm => ({
  name: lead.name || "",
  phone: lead.phone || "",
  zalo: lead.zalo || "",
  category: lead.category || "",
  estimatedBudget: lead.estimatedBudget || "",
  status: lead.status,
  source: lead.source || "SEO",
  description: lead.description || "",
});

const LeadDetail = ({ lead }: { lead: Lead }) => {
  const links = parseLeadLinks(lead.fromLink);
  const rows = [
    { label: "Tên", value: lead.name || "--" },
    { label: "Số điện thoại", value: lead.phone || "--" },
    { label: "Zalo", value: lead.zalo || "--" },
    { label: "Lĩnh vực", value: lead.category || "--" },
    { label: "Ngân sách", value: lead.estimatedBudget || "--" },
    { label: "Trạng thái", value: leadStatusOptions.find((option) => option.value === lead.status)?.label || "--" },
    { label: "Nguồn", value: leadSourceLabel[lead.source] || "--" },
    { label: "Mô tả", value: lead.description || "--" },
    { label: "Ngày tạo", value: formatDateTime(lead.createdAt) },
  ];

  return (
    <div>
      <div className="leadModal__title">Thông tin lead</div>
      <div className="leadModal__grid">
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <span className="leadModal__label">{row.label}</span>
            <span>{row.value}</span>
          </React.Fragment>
        ))}
        {links.length > 0 && (
          <>
            <span className="leadModal__label">Đường dẫn</span>
            <LeadFromLinks fromLink={lead.fromLink} />
          </>
        )}
      </div>
    </div>
  );
};

export const LeadModal = observer(
  React.forwardRef(({ onClose, onSubmitOk }: LeadModalProps, ref) => {
    const [form] = Form.useForm<LeadForm>();
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [status, setStatus] = useState<ModalStatus>("create");
    const [selectedLead, setSelectedLead] = useState<Lead>();

    const canUpdate = can(LEAD_PATH, "UPDATE");

    const fillLead = useCallback(
      (lead: Lead) => {
        form.setFieldsValue(toForm(lead));
        setSelectedLead(lead);
      },
      [form],
    );

    useImperativeHandle<any, LeadModal>(
      ref,
      () => ({
        handleCreate() {
          form.resetFields();
          form.setFieldsValue({ status: "PENDING", source: "SEO" });
          setSelectedLead(undefined);
          setStatus("create");
          setVisible(true);
        },
        handleUpdate(lead: Lead) {
          fillLead(lead);
          setStatus("update");
          setVisible(true);
        },
        handleView(lead: Lead) {
          fillLead(lead);
          setStatus("view");
          setVisible(true);
        },
      }),
      [fillLead, form],
    );

    const handleClose = useCallback(() => {
      setVisible(false);
      setSelectedLead(undefined);
      onClose();
    }, [onClose]);

    const handleStartEdit = useCallback(() => {
      setStatus("update");
    }, []);

    const buildPayload = useCallback(() => {
      const values = form.getFieldsValue();
      return {
        lead: {
          name: (values.name || "").trim(),
          phone: (values.phone || "").trim(),
          zalo: (values.zalo || "").trim(),
          category: (values.category || "").trim(),
          estimatedBudget: (values.estimatedBudget || "").trim(),
          status: values.status || "PENDING",
          source: values.source || "SEO",
          fromLink: selectedLead?.fromLink || "",
          description: values.description || "",
        },
      };
    }, [form, selectedLead]);

    const handleSubmit = useCallback(async () => {
      await form.validateFields();
      const data = buildPayload();
      setLoading(true);
      try {
        if (status === "create") {
          await leadApi.create(data);
        } else {
          await leadApi.update(selectedLead?.id || 0, data);
        }
        message.success(status === "create" ? "Tạo mới thành công!" : "Cập nhật thành công!");
        handleClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    }, [buildPayload, form, handleClose, onSubmitOk, selectedLead?.id, status]);

    const title = status === "create" ? "Tạo lead" : status === "update" ? "Cập nhật lead" : "Xem lead";

    return (
      <Modal
        className="leadModal"
        onCancel={handleClose}
        open={visible}
        title={title}
        style={{ top: 20 }}
        width={720}
        confirmLoading={loading}
        footer={
          status === "view" ? (
            <div className="leadModal__footer">
              <AppButton onClick={handleClose}>Đóng</AppButton>
              {canUpdate && (
                <AppButton tone="primary" onClick={handleStartEdit}>
                  Sửa
                </AppButton>
              )}
            </div>
          ) : (
            <div className="leadModal__footer">
              <AppButton onClick={handleClose}>Hủy</AppButton>
              <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
                Lưu
              </AppButton>
            </div>
          )
        }
      >
        {status === "view" && selectedLead ? (
          <LeadDetail lead={selectedLead} />
        ) : (
          <Form layout="vertical" form={form}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Tên" name="name">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Số điện thoại" name="phone" rules={requiredRules}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Zalo" name="zalo">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Lĩnh vực" name="category">
                  <Input maxLength={255} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Ngân sách dự kiến" name="estimatedBudget">
                  <Input maxLength={255} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Trạng thái" name="status" rules={requiredRules}>
                  <AppSelect options={leadStatusOptions} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Nguồn" name="source" rules={requiredRules}>
                  <AppSelect options={leadSourceOptions} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Mô tả" name="description">
                  <Input.TextArea rows={4} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>
    );
  }),
);
